"use client";

// 新しい版が出ていたら知らせる帯。
//
// 由来: 長時間開いたままの画面は古い JS で動き続ける。デプロイしても
// 読み込み直さない限り新しいビルドに乗らないため、修正済みの不具合が
// 数日にわたり報告され続けた (報告のスタックが指すチャンクは、既に
// サーバーから消えていた)。
//
// 勝手に再読み込みはしない。入力中のフォームが飛ぶ方が困るので、
// 押したときだけ更新する。判定は「画面に戻ってきたとき」と 30 分ごと
// (放置されている画面がいつまでも古いままにならない程度の間隔)。

import { useCallback, useEffect, useState } from "react";

/** 定期確認の間隔。放置画面向けなので短くしすぎない。 */
const CHECK_INTERVAL_MS = 30 * 60 * 1000;

export default function UpdateBanner() {
  const [stale, setStale] = useState(false);

  const check = useCallback(async () => {
    // ビルド時に焼き込まれた「この画面の版」。未設定 (dev 等) なら何もしない。
    const mine = process.env.NEXT_PUBLIC_APP_VERSION;
    if (!mine) return;
    try {
      const r = await fetch("/api/version", { cache: "no-store" });
      if (!r.ok) return;
      const body = (await r.json()) as { version?: string | null };
      if (body.version && body.version !== mine) setStale(true);
    } catch {
      /* 取得できないときは黙って見送る (オフライン等) */
    }
  }, []);

  useEffect(() => {
    // 初回はマウント直後ではなく少し置いてから。effect の中で同期に
    // setState を起こさない (react-hooks/set-state-in-effect) ためと、
    // 起動直後の通信を 1 本増やさないため。
    const first = setTimeout(check, 3000);
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", check);
    const id = setInterval(check, CHECK_INTERVAL_MS);
    return () => {
      clearTimeout(first);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", check);
      clearInterval(id);
    };
  }, [check]);

  if (!stale) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[9500] flex justify-center px-3 pb-3 pointer-events-none">
      <div className="pointer-events-auto flex items-center gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2.5 shadow-lg dark:border-blue-800 dark:bg-blue-950">
        <span className="text-sm text-blue-900 dark:text-blue-100">
          新しいバージョンがあります
        </span>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="shrink-0 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-blue-500"
        >
          更新する
        </button>
      </div>
    </div>
  );
}
